/**
 * Template Listing Slice
 *
 * Manages flight listing template state including:
 * - Review loading state
 * - Preferences filters (bitset-based filtering)
 * - Autosuggest state
 *
 * This slice is specifically for the flights-listing template.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LocationSuggestion } from '@contracts';
import { fetchFlightInterstitialThunk } from '../thunks/interstitialThunks';
import { fetchAutosuggestThunk } from '../thunks/autosuggestThunks';

// ============================================
// Preferences Types (BitSet-based filtering)
// ============================================

export interface BitSetFilter {
  id: string;
  name: string;
  category: string;
  price: string | null;
  icon?: string | undefined;
  bitSet: string;
  matchCount?: number | undefined;
  isSelected: boolean;
  timeRange?: { start: string; end: string } | undefined;
}

export interface PreferencesState {
  selectedFilters: BitSetFilter[];
  combinedBitSet: string | null;
}

// ============================================
// Autosuggest State
// ============================================

export interface AutosuggestState {
  suggestions: LocationSuggestion[];
  isLoading: boolean;
  error: string | null;
}

// ============================================
// State Types
// ============================================

export interface TemplateListingState {
  isReviewLoading: boolean;
  preferences: PreferencesState;
  autosuggest: AutosuggestState;
}

// ============================================
// Initial State
// ============================================

export const initialPreferencesState: PreferencesState = {
  selectedFilters: [],
  combinedBitSet: null,
};

export const initialAutosuggestState: AutosuggestState = {
  suggestions: [],
  isLoading: false,
  error: null,
};

export const initialTemplateListingState: TemplateListingState = {
  isReviewLoading: false,
  preferences: initialPreferencesState,
  autosuggest: initialAutosuggestState,
};

// ============================================
// BitSet Utility Functions
// ============================================

/**
 * Combines two bitsets using OR operation
 * Used for filters within the same category
 */
const combineBitSetsWithOR = (bitSet1: string, bitSet2: string): string => {
  const maxLength = Math.max(bitSet1.length, bitSet2.length);
  return Array.from({ length: maxLength }, (_, index) => {
    const bit1 = bitSet1[index] ?? '0';
    const bit2 = bitSet2[index] ?? '0';
    return bit1 === '1' || bit2 === '1' ? '1' : '0';
  }).join('');
};

/**
 * Combines two bitsets using AND operation
 * Used for combining results across different categories
 */
const combineBitSetsWithAND = (bitSet1: string, bitSet2: string): string => {
  const maxLength = Math.max(bitSet1.length, bitSet2.length);
  return Array.from({ length: maxLength }, (_, index) => {
    const bit1 = bitSet1[index] ?? '0';
    const bit2 = bitSet2[index] ?? '0';
    return bit1 === '1' && bit2 === '1' ? '1' : '0';
  }).join('');
};

/**
 * Calculates the combined bitset from selected filters
 * - OR within same category
 * - AND across different categories
 */
const calculateCombinedBitSet = (selectedFilters: BitSetFilter[]): string | null => {
  if (selectedFilters.length === 0) return null;

  // Group filters by category
  const filtersByCategory = new Map<string, BitSetFilter[]>();
  selectedFilters.forEach((filter) => {
    const category = filter.category;
    const categoryFilters = filtersByCategory.get(category);
    if (categoryFilters) {
      categoryFilters.push(filter);
    } else {
      filtersByCategory.set(category, [filter]);
    }
  });

  // Calculate OR within each category
  const categoryBitSets: string[] = [];
  filtersByCategory.forEach((categoryFilters) => {
    if (categoryFilters.length === 0) return;

    const firstFilter = categoryFilters[0];
    if (!firstFilter) return;

    let categoryBitSet = firstFilter.bitSet;
    for (let i = 1; i < categoryFilters.length; i++) {
      const currentFilter = categoryFilters[i];
      if (currentFilter) {
        categoryBitSet = combineBitSetsWithOR(categoryBitSet, currentFilter.bitSet);
      }
    }
    categoryBitSets.push(categoryBitSet);
  });

  if (categoryBitSets.length === 0) return null;

  // Calculate AND across categories
  const firstBitSet = categoryBitSets[0];
  if (!firstBitSet) return null;

  let combined = firstBitSet;
  for (let i = 1; i < categoryBitSets.length; i++) {
    const currentBitSet = categoryBitSets[i];
    if (currentBitSet) {
      combined = combineBitSetsWithAND(combined, currentBitSet);
    }
  }

  return combined;
};

// ============================================
// Slice
// ============================================

const templateListingSlice = createSlice({
  name: 'flights/templateListing',
  initialState: initialTemplateListingState,
  reducers: {
    setReviewLoading: (state, action: PayloadAction<boolean>) => {
      state.isReviewLoading = action.payload;
    },

    // ============================================
    // Preferences Actions
    // ============================================

    /**
     * Toggle a filter selection (add if not selected, remove if selected)
     */
    togglePreferenceFilter: (state, action: PayloadAction<BitSetFilter>) => {
      const filter = action.payload;
      const existingIndex = state.preferences.selectedFilters.findIndex((f) => f.id === filter.id);

      if (existingIndex >= 0) {
        // Remove filter
        state.preferences.selectedFilters.splice(existingIndex, 1);
      } else {
        // Add filter with isSelected = true
        state.preferences.selectedFilters.push({ ...filter, isSelected: true });
      }

      // Recalculate combined bitset
      state.preferences.combinedBitSet = calculateCombinedBitSet(state.preferences.selectedFilters);
    },

    /**
     * Remove a filter by ID
     */
    removePreferenceFilter: (state, action: PayloadAction<string>) => {
      const filterId = action.payload;
      state.preferences.selectedFilters = state.preferences.selectedFilters.filter(
        (f) => f.id !== filterId,
      );

      // Recalculate combined bitset
      state.preferences.combinedBitSet = calculateCombinedBitSet(state.preferences.selectedFilters);
    },

    /**
     * Clear all selected filters
     */
    clearAllPreferenceFilters: (state) => {
      state.preferences.selectedFilters = [];
      state.preferences.combinedBitSet = null;
    },

    /**
     * Set all selected filters at once (for initialization or bulk updates)
     */
    setPreferenceFilters: (state, action: PayloadAction<BitSetFilter[]>) => {
      state.preferences.selectedFilters = action.payload;
      state.preferences.combinedBitSet = calculateCombinedBitSet(action.payload);
    },

    /**
     * Reset preferences to initial state
     */
    resetPreferences: (state) => {
      state.preferences = initialPreferencesState;
    },

    /** Reset listing state to initial values */
    resetTemplateListingState: () => initialTemplateListingState,

    /**
     * Clear autosuggest suggestions
     */
    clearAutosuggestSuggestions: (state) => {
      state.autosuggest.suggestions = [];
      state.autosuggest.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Flight Interstitial (track loading state)
    builder
      .addCase(fetchFlightInterstitialThunk.pending, (state) => {
        state.isReviewLoading = true;
      })
      .addCase(fetchFlightInterstitialThunk.fulfilled, (state) => {
        state.isReviewLoading = false;
      })
      .addCase(fetchFlightInterstitialThunk.rejected, (state) => {
        state.isReviewLoading = false;
      });

    // Fetch Autosuggest
    builder
      .addCase(fetchAutosuggestThunk.pending, (state) => {
        state.autosuggest.isLoading = true;
        state.autosuggest.error = null;
      })
      .addCase(fetchAutosuggestThunk.fulfilled, (state, action) => {
        state.autosuggest.isLoading = false;
        state.autosuggest.suggestions = action.payload;
        state.autosuggest.error = null;
      })
      .addCase(fetchAutosuggestThunk.rejected, (state, action) => {
        state.autosuggest.isLoading = false;
        state.autosuggest.error = action.payload ?? 'Failed to fetch suggestions';
        state.autosuggest.suggestions = [];
      });
  },
});

// ============================================
// Actions
// ============================================

export const {
  setReviewLoading,
  resetTemplateListingState,
  // Preferences actions
  togglePreferenceFilter,
  removePreferenceFilter,
  clearAllPreferenceFilters,
  setPreferenceFilters,
  resetPreferences,
  // Autosuggest actions
  clearAutosuggestSuggestions,
} = templateListingSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const templateListingReducer = templateListingSlice.reducer;
